import React from "react";
import Icon from "../../../components/AppIcon"; // Custom Icon component
import Image from "../../../components/AppImage"; // Custom Image component

// Data for the About section
const aboutData = {
  image: "https://rehmat-online.vercel.app/assets/images/image.png", // Profile image URL
  imageAlt: "Rehmat Ullah Pic", // Alt text for accessibility
  bio: [
    "I’m Rehmat Ullah, a passionate Front-End Engineer and sixth-semester Software Engineering student at Islamia University of Bahawalpur. I specialize in building modern, responsive, and user-focused web applications using React.js, Angular, TypeScript, JavaScript, and Tailwind CSS.",
    "I have hands-on experience developing real-world projects such as portfolio websites, API-driven applications, and responsive business platforms. I focus on creating clean UI components, optimized performance, and scalable front-end architectures that deliver smooth, accessible user experiences.",
    "Through internships and team-based projects, I’ve strengthened my skills in Git, GitHub workflows, and modern development practices. I continuously learn new technologies and aim to grow into a full-stack engineer with AI integration, building impactful and meaningful digital products."
  ],
  achievements: [
    { icon: "Award", label: "Web Development", year: "2024" },
    { icon: "Star", label: "Meta Front-End Developer Certificate", year: "2023" },
    { icon: "Award", label: "Python Developer", year: "2025" },
    { icon: "Award", label: "Recommendation Letter (CodeAlpha)", year: "2025" }
  ],
  highlights: [
    { icon: "Code2", label: "React.js & JavaScript Developer" },
    { icon: "Palette", label: "Tailwind CSS & Responsive Design" },
    { icon: "GitBranch", label: "Git & GitHub Collaboration" },
    { icon: "Globe", label: "Modern Front-End Web Development" }
  ]
};

export default function AboutSection() {
  return (
    <section
      id="about"
      className="py-16 md:py-20" // Padding for vertical spacing
      style={{ background: "var(--color-card)" }} // Section background
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12">
          <p
            className="text-xs font-semibold tracking-widest uppercase mb-2"
            style={{
              color: "var(--color-primary)",
              fontFamily: "var(--font-caption)"
            }}
          >
            Get To Know Me
          </p>

          <h2
            className="text-3xl md:text-4xl font-bold"
            style={{
              fontFamily: "var(--font-heading)",
              color: "var(--color-foreground)"
            }}
          >
            About Me
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          
          {/* Image Section */}
          <div className="flex-shrink-0 w-full lg:w-auto flex justify-center">
            <div className="relative w-64 md:w-72 lg:w-80">
              {/* Image container with border */}
              <div
                className="overflow-hidden rounded-xl"
                style={{
                  height: "360px",
                  border: "1px solid rgba(124,106,247,0.2)"
                }}
              >
                <Image
                  src={aboutData?.image}
                  alt={aboutData?.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Background accent behind image */}
              <div
                className="absolute -bottom-3 -left-3 w-full h-full rounded-xl -z-10"
                style={{
                  background: "rgba(124,106,247,0.08)",
                  border: "1px solid rgba(124,106,247,0.15)"
                }}
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 min-w-0">
            
            {/* Bio Paragraphs */}
            <div className="space-y-4 mb-8">
              {aboutData?.bio?.map((para, i) => (
                <p
                  key={i}
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: "var(--color-muted-foreground)" }}
                >
                  {para}
                </p>
              ))}
            </div>

            {/* Highlights / Skills */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {aboutData?.highlights?.map((h) => (
                <div
                  key={h?.label}
                  className="flex items-center gap-2 p-3 rounded-lg border transition-all duration-200 hover:border-primary"
                  style={{
                    borderColor: "var(--color-border)",
                    background: "var(--color-background)"
                  }}
                >
                  <Icon
                    name={h?.icon}
                    size={16}
                    color="var(--color-primary)"
                  />
                  <span
                    className="text-xs md:text-sm font-medium"
                    style={{
                      color: "var(--color-foreground)",
                      fontFamily: "var(--font-caption)"
                    }}
                  >
                    {h?.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Achievements / Certifications */}
            <div>
              <h3
                className="text-sm font-semibold mb-4 uppercase tracking-wider"
                style={{
                  color: "var(--color-muted-foreground)",
                  fontFamily: "var(--font-caption)"
                }}
              >
                Certifications & Education
              </h3>

              <div className="space-y-3">
                {aboutData?.achievements?.map((a) => (
                  <div key={a?.label} className="flex items-center gap-3">
                    
                    {/* Icon for achievement */}
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ background: "rgba(124,106,247,0.12)" }}
                    >
                      <Icon
                        name={a?.icon}
                        size={16}
                        color="var(--color-primary)"
                      />
                    </div>

                    {/* Achievement label */}
                    <div className="flex-1 min-w-0">
                      <span
                        className="text-sm font-medium"
                        style={{ color: "var(--color-foreground)" }}
                      >
                        {a?.label}
                      </span>
                    </div>

                    {/* Year / timeline */}
                    <span
                      className="text-xs flex-shrink-0"
                      style={{
                        color: "var(--color-muted-foreground)",
                        fontFamily: "var(--font-data)"
                      }}
                    >
                      {a?.year}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}